const AUDIO_PROCESSING_URL = `${process.env.PUBLIC_URL || ''}/audio-processing.js`;
const STT_CONFIG_URL = '/insights/stt/config';
const DEFAULT_STT_CONFIG = {
  sampleRateHz: 16000,
  maxRecordingLengthMs: 120_000,
  stopGraceMs: 1500,
  connectTimeoutMs: 15_000,
};

let sttConfigPromise = null;

/**
 * Fetch the shared STT config. Falls back to defaults on failure.
 * @return {Promise<Object>}
 */
function loadSttConfig() {
  if (!sttConfigPromise) {
    sttConfigPromise = fetch(STT_CONFIG_URL, {credentials: 'include'})
      .then((res) => {
        if (!res.ok) {
          throw new Error(`stt config HTTP ${res.status}`);
        }
        return res.json();
      })
      .then((config) => {
        return {...DEFAULT_STT_CONFIG, ...config};
      })
      .catch((err) => {
        console.warn('[SpeechToText] using default config; fetch failed', err);
        sttConfigPromise = null;
        return DEFAULT_STT_CONFIG;
      });
  }
  return sttConfigPromise;
}

/**
 * @param {Object} callbacks
 * @param {Function=} callbacks.onPartial
 * @param {Function=} callbacks.onFinal
 * @param {Function=} callbacks.onError
 * @param {Function=} callbacks.onEnd
 * @return {{start: function(): Promise, stop: function(): undefined}}
 */
export function createSpeechStreamingClient({onPartial, onFinal, onError, onEnd}) {
  let ws = null;
  let ctx = null;
  let stream = null;
  let src = null;
  let node = null;
  let killTimer = null;
  let stopGraceTimer = null;
  let config = DEFAULT_STT_CONFIG;

  const cleanupAudio = () => {
    if (killTimer) {
      clearTimeout(killTimer);
      killTimer = null;
    }
    if (stopGraceTimer) {
      clearTimeout(stopGraceTimer);
      stopGraceTimer = null;
    }
    try {
      if (node) {
        node.port.onmessage = null;
        node.disconnect();
      }
    } catch (cleanupErr) {
      console.warn(`[SpeechToText] node cleanup failed`, cleanupErr);
    }
    try {
      if (src) {
        src.disconnect();
      }
    } catch (cleanupErr) {
      console.warn(`[SpeechToText] source cleanup failed`, cleanupErr);
    }
    try {
      if (stream) {
        stream.getTracks().forEach((t) => {
          t.stop();
        });
      }
    } catch (cleanupErr) {
      console.warn(`[SpeechToText] stream cleanup failed`, cleanupErr);
    }
    try {
      if (ctx && ctx.state !== 'closed') {
        ctx.close();
      }
    } catch (cleanupErr) {
      console.warn(`[SpeechToText] audio context cleanup failed`, cleanupErr);
    }
    node = null;
    src = null;
    stream = null;
    ctx = null;
  };

  const closeWs = () => {
    if (!ws) {
      return;
    }
    const socket = ws;
    ws = null;

    const closeSocket = () => {
      if (stopGraceTimer) {
        clearTimeout(stopGraceTimer);
        stopGraceTimer = null;
      }
      try {
        if (socket.readyState !== WebSocket.CLOSED && socket.readyState !== WebSocket.CLOSING) {
          socket.close();
        }
      } catch (closeErr) {
        console.warn(`[SpeechToText] ws close failed`, closeErr);
      }
    };

    try {
      if (socket.readyState === WebSocket.OPEN) {
        socket.send(JSON.stringify({type: 'stop'}));
      }
    } catch (sendErr) {
      console.warn(`[SpeechToText] stop send failed`, sendErr);
    }

    // Best case: the server drains the final transcript and closes the socket
    // itself — close as soon as that happens (no fixed wait). The grace timer is
    // only a fallback ceiling in case the server never closes.
    socket.addEventListener('close', closeSocket);
    stopGraceTimer = setTimeout(closeSocket, config.stopGraceMs);
  };

  async function start() {
    try {
      await startInternal();
    } catch (startErr) {
      cleanupAudio();
      if (ws) {
        const socket = ws;
        ws = null;
        try {
          socket.close();
        } catch (closeErr) {
          console.warn(`[SpeechToText] ws close failed`, closeErr);
        }
      }
      throw startErr;
    }
  }

  async function startInternal() {
    config = await loadSttConfig();

    stream = await navigator.mediaDevices.getUserMedia({
      audio: {channelCount: 1, echoCancellation: true, noiseSuppression: true},
    });

    ctx = new AudioContext();
    await ctx.audioWorklet.addModule(AUDIO_PROCESSING_URL);

    src = ctx.createMediaStreamSource(stream);
    node = new AudioWorkletNode(ctx, 'audio-processor', {
      processorOptions: {targetSampleRate: config.sampleRateHz},
    });
    src.connect(node);
    // Worklet processors only run when the graph is pulled; mute output to keep it alive.
    const mute = ctx.createGain();
    mute.gain.value = 0;
    node.connect(mute);
    mute.connect(ctx.destination);

    // For development in localhost, likely need to flip
    // const protocol = `ws://localhost:${'8080'}`;

    const protocol = window.location.protocol === 'https:' ? 'wss' : 'ws';
    const wsUrl = `${protocol}://${window.location.host}/insights/stt/stream`;

    ws = new WebSocket(wsUrl);
    ws.binaryType = 'arraybuffer';

    // Audio is only forwarded once the full chain is live (our server has
    // connected to AssemblyAI and relayed `begin`).
    let serverReady = false;

    node.port.onmessage = (e) => {
      if (serverReady && ws && ws.readyState === WebSocket.OPEN) {
        ws.send(e.data);
      }
    };

    // Resolve start() only when the server relays `begin` (chain ready).
    await new Promise((resolve, reject) => {
      const socket = ws;
      let settled = false;
      const finish = (fn, value) => {
        if (settled) {
          return;
        }
        settled = true;
        clearTimeout(connectTimer);
        fn(value);
      };

      const connectTimer = setTimeout(() => {
        finish(reject, new Error(`STT session not ready after ${config.connectTimeoutMs}ms`));
      }, config.connectTimeoutMs);

      ws.onmessage = (e) => {
        let msg;
        try {
          msg = JSON.parse(e.data);
        } catch (parseErr) {
          console.warn(`[SpeechToText] JSON parse error - `, parseErr);
          return;
        }
        if (msg.type === 'begin') {
          serverReady = true;
          finish(resolve);
        } else if (msg.type === 'partial' && msg.transcript != null) {
          onPartial?.(msg.transcript);
        } else if (msg.type === 'final' && msg.transcript != null) {
          onFinal?.(msg.transcript);
        } else if (msg.type === 'error') {
          finish(reject, new Error(msg.message || 'Transcription error'));
          onError?.({code: 'stt_error', message: msg.message || 'Transcription error'});
        }
      };

      socket.addEventListener('error', () => {
        finish(reject, new Error('WebSocket connection failed'));
      });
      socket.addEventListener('close', (event) => {
        finish(reject, new Error(`WebSocket closed before ready (${event.code})`));
      });
    });

    ws.onerror = (event) => {
      onError?.({code: 'ws_error', message: 'WebSocket error'});
    };

    ws.onclose = (event) => {
      onEnd?.();
    };

    killTimer = setTimeout(() => {
      onError?.({
        code: 'timeout',
        message: 'Maximum recording time of 2 minutes reached. Recording stopped.',
      });
      stop();
    }, config.maxRecordingLengthMs);
  }

  function stop() {
    try {
      if (node?.port) {
        node.port.postMessage({type: 'flush'});
        // Allow the final padded frame to reach the socket before teardown.
        setTimeout(() => {
          cleanupAudio();
          closeWs();
        }, 100);
        return;
      }
    } catch (flushErr) {
      console.warn(`[SpeechToText] flush failed`, flushErr);
    }
    cleanupAudio();
    closeWs();
  }

  return {start, stop};
}
